import { useCallback } from 'react';
import { useClient } from 'urql';
import {
  type DeliveryOrderDetail,
  OrderPaymentStatus,
} from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';

import {
  checkIfFailureWasAlreadyShown,
  getLocalStorageOrderId,
  type OrderingContext,
} from '@order/features/ordering';

import { type PartialLineItem } from '../../../../graphql';
import {
  BagPaymentStateDocument,
  type BagPaymentStateQuery,
} from '../../GraphQL/BagPaymentState.generated';

export const useFetchOrderPaymentState = () => {
  const client = useClient();

  const fetchOrderPaymentState = useCallback(
    async (context: OrderingContext<PartialLineItem, DeliveryOrderDetail>) => {
      const orderId = context.orderId || (await getLocalStorageOrderId());
      const query = client.query<BagPaymentStateQuery>;

      if (!orderId) throw new Error('Order ID is required');

      const response = await query(
        BagPaymentStateDocument,
        { orderId, orderType },
        { requestPolicy },
      ).toPromise();

      const maybeData = response.data?.orderPaymentState;
      const maybeUpdatedAt = Number(maybeData?.updatedAt);

      const paymentIntentId = maybeData?.paymentIntentId;
      const isFailure = maybeData?.status === OrderPaymentStatus.Failed;
      const hasAlreadyShownFailure = isFailure ? await checkIfFailureWasAlreadyShown(paymentIntentId) : false; // prettier-ignore

      const paymentStateExpiration = Number.isNaN(maybeUpdatedAt) ? undefined : maybeUpdatedAt; // prettier-ignore
      const paymentStateStatus = hasAlreadyShownFailure ? OrderPaymentStatus.Released : maybeData?.status; // prettier-ignore
      const paymentStateError = hasAlreadyShownFailure ? undefined : maybeData?.error; // prettier-ignore

      return {
        order: { id: orderId, hasEarnedLoyaltyPoints: false },
        paymentStateExpiration,
        paymentStateStatus,
        paymentStateError,
      };
    },
    [client.query],
  );

  return { fetchOrderPaymentState };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const requestPolicy = 'network-only' as const;
const orderType = OrderTypeValues.GravyOrder;
